import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CompanySuggestion } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { TextInput } from '@/components/ui/Field';
import styles from './Leads.module.css';

export type CompanySelection = {
  account_id: string | null;
  name: string;
  website: string | null;
  industry: string | null;
  company_size: string | null;
};

type Props = {
  projectId: string;
  value: string;
  onValueChange: (name: string) => void;
  onSelect: (selection: CompanySelection) => void;
  onBlur?: () => void;
  placeholder?: string;
};

export function CompanyAutocomplete({
  projectId,
  value,
  onValueChange,
  onSelect,
  onBlur,
  placeholder = 'Zoek bedrijf (min. 3 letters)…',
}: Props) {
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(value.trim()), 280);
    return () => window.clearTimeout(t);
  }, [value]);

  const suggestQ = useQuery({
    queryKey: ['accounts-autocomplete', projectId, debouncedQ],
    enabled: debouncedQ.length >= 3,
    queryFn: () =>
      apiFetch<{ suggestions: CompanySuggestion[] }>(
        `/api/accounts/autocomplete?project_id=${projectId}&q=${encodeURIComponent(debouncedQ)}`,
      ),
    staleTime: 30_000,
  });

  const suggestions = suggestQ.data?.suggestions ?? [];
  const showList = open && debouncedQ.length >= 3 && (suggestions.length > 0 || suggestQ.isFetching);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function pick(s: CompanySuggestion) {
    onSelect({
      account_id: s.account_id,
      name: s.name,
      website: s.website,
      industry: s.industry,
      company_size: s.company_size,
    });
    onValueChange(s.name);
    setOpen(false);
  }

  return (
    <div className={styles.autocompleteWrap} ref={wrapRef}>
      <TextInput
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onValueChange(e.target.value);
          setOpen(true);
        }}
        onBlur={() => onBlur?.()}
      />
      {showList ? (
        <ul className={styles.autocompleteList} id={listId} role="listbox">
          {suggestQ.isFetching && !suggestions.length ? (
            <li className={styles.autocompleteHint}>Zoeken…</li>
          ) : null}
          {suggestions.map((s) => (
            <li key={`${s.source}-${s.account_id ?? s.name}`}>
              <button
                type="button"
                className={styles.autocompleteItem}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(s)}
              >
                <span className={styles.autocompleteName}>{s.name}</span>
                {s.website ? <span className={styles.autocompleteMeta}>{s.website}</span> : null}
                <span className={styles.autocompleteBadge}>
                  {s.source === 'account' ? 'Account' : 'Lead'}
                </span>
              </button>
            </li>
          ))}
          {!suggestQ.isFetching && !suggestions.length ? (
            <li className={styles.autocompleteHint}>Geen treffers</li>
          ) : null}
        </ul>
      ) : null}
      {value.trim().length > 0 && value.trim().length < 3 ? (
        <p className={styles.autocompleteHintInline}>Typ nog {3 - value.trim().length} letter(s)</p>
      ) : null}
    </div>
  );
}
